import {
  Autocomplete,
  Card,
  CardHeader,
  CircularProgress,
  Grid,
  TextField as MuiTextField,
  TableContainer,
} from "@mui/material";

import { useMutation } from "@tanstack/react-query";
import { Button as AntButton, Space, Table, Tooltip } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import MDBox from "~/components/MDBox";
import MDButton from "~/components/MDButton";
import DashboardLayout from "~/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "~/examples/Navbars/DashboardNavbar";

import { EditOutlined, ImageNotSupportedOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/AddOutlined";
import moment from "moment";
import AuthorAvatar from "~/pages/components/AuthorAvatar";
import { usePlantDetails } from "~/queries/plantMasterQuery";
import { getPlantSuggestions } from "~/services/plant.service";
import { getImageUrl } from "~/utils/utils";
import { debounce } from "./helper";
import MultipleAvatar from "../components/MultipleAvatar";

const itemsPerPage = 5;

const PlantMonitoringTable = () => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const plantDetailsMutation = usePlantDetails();

  const { data, mutateAsync, isLoading } = useMutation({
    mutationKey: ["plantSuggestions", searchQuery],
    mutationFn: (searchParams) => getPlantSuggestions(searchParams),
    onSuccess: (data) => {
      // Modify the data response here
      setOptions(
        data?.data?.map((item) => ({
          label: item.BarCode_Serial_No,
          value: item.BarCode_Serial_No,
        }))
      );
    },
  });

  function handleAutocompleteChange(value, action = false) {
    if (
      (value === undefined && value?.label === undefined) ||
      value?.label === "" ||
      value === null
    ) {
      // formRef.current.setFieldValue(fieldName.trim(), '');
    } else {
      // do something else

      plantDetailsMutation.mutateAsync(value.label);
      //   monitoringCategoryMutation.mutateAsync();
    }
  }

  function handleSuggestionInput(searchText) {
    if (searchText === "") return;
    setSearchQuery(searchText);
    mutateAsync(searchText);
  }

  const debounceHandlerForSuggestions = useCallback(
    debounce(handleSuggestionInput, 500),
    []
  );

  const columns = [
    {
      title: "Plant Image",
      key: "Plant_Image",
      responsive: ["lg", "md"],
      width: 150,
      render: (text, record) => {
        const imageUrls = record.Plant_Img ? record.Plant_Img.split(",").map(getImageUrl) : [];
        return (
          <MultipleAvatar
            images={imageUrls}
            icon={
              record.Plant_Img === null ? <ImageNotSupportedOutlined /> : null
            }
          />
        );
      },
      fixed: "left",
    },
    {
      title: "Plant Monitoring Type",
      dataIndex: "Plantation_Monitoring_Type",
      key: "Plantation_Monitoring_Type",
      responsive: ["lg", "md", "xl", "sm"],
      fixed: "left",
    },
    {
      title: "Plant Growth Height (m)",
      dataIndex: "PlantGrowth_Height",
      key: "PlantGrowth_Height",
      responsive: ["lg", "md", "xl", "sm"],
    },
    {
      title: "Plant Collar Diameter (cm)",
      dataIndex: "PlantGrowth_Collar_Diameter",
      key: "PlantGrowth_Collar_Diameter",
      responsive: ["lg", "md", "xl", "sm"],
    },
    {
      title: "Canopy Cover (cm)",
      dataIndex: "Canopy_cover",
      key: "Canopy_cover",
      responsive: ["lg", "md", "xl", "sm"],
    },
    {
      title: "Moisture Content (%)",
      dataIndex: "Moisture_content",
      key: "Moisture_content",
      responsive: ["lg", "md", "xl", "sm"],
    },
    {
      title: "Survivability",
      dataIndex: "Survivability",
      key: "Survivability",
      responsive: ["lg", "md", "xl", "sm"],
    },
    // new changes - tag v3.0.1
    {
      title: "Nitrogen (mg)",
      dataIndex: "Nitrogen",
      key: "Nitrogen",
      responsive: ["lg", "md", "xl", "sm"],
    },
    // {
    //   title: "Plant Status",
    //   dataIndex: "Plant_Status",
    //   key: "Plant_Status",
    //   responsive: ["lg", "md", "xl", "sm"],
    // },
    {
      title: "Phosphate (mg)",
      dataIndex: "Phosphate",
      key: "Phosphate",
      responsive: ["lg", "md", "xl", "sm"],
    },
    {
      title: "Potassium (mg)",
      dataIndex: "Potassium",
      key: "Potassium",
      responsive: ["lg", "md", "xl", "sm"],
    },
    {
      title: "Soil (ph)",
      dataIndex: "Soil_PH",
      key: "Soil_PH",
      responsive: ["lg", "md", "xl", "sm"],
    },
    {
      title: "Soil Temperature (c)",
      dataIndex: "Soil_Temperature",
      key: "Soil_Temperature",
      responsive: ["lg", "md", "xl", "sm"],
    },
    {
      title: "Soil Moisture (%)",
      dataIndex: "Soil_Moisture",
      key: "Soil_Moisture",
      responsive: ["lg", "md", "xl", "sm"],
    },
    {
      title: "Electrical Conductivity (ds/cm)",
      dataIndex: "Electrical_Conductivity",
      key: "Electrical_Conductivity",
      responsive: ["lg", "md", "xl", "sm"],
    },

    {
      title: "PlantGrowth Flower Count",
      dataIndex: "PlantGrowth_Flower_Count",
      key: "PlantGrowth_Flower_Count",
      responsive: ["lg", "md", "xl", "sm"],
    },
    {
      title: "PlantGrowth Fruit Count",
      dataIndex: "PlantGrowth_Fruit_Count",
      key: "PlantGrowth_Fruit_Count",
      responsive: ["lg", "md", "xl", "sm"],
    },
    {
      title: "Monitoring Remark",
      dataIndex: "Monitoring_Remark",
      key: "Monitoring_Remark",
      responsive: ["lg", "md", "xl", "sm"],
    },

    {
      title: "Flower Bud Formation",
      dataIndex: "Flower_Bud_Formation",
      key: "Flower_Bud_Formation",
      responsive: ["lg", "md", "xl", "sm"],
      render: (record) =>
        record === null ? "" : moment(record).format("DD/MM/YYYY"),
    },
    {
      title: "Fruit Seed Fall",
      dataIndex: "Fruit_Seed_Fall",
      key: "Fruit_Seed_Fall",
      responsive: ["lg", "md", "xl", "sm"],
      render: (record) =>
        record === null ? "" : moment(record).format("DD/MM/YYYY"),
    },
    {
      title: "Fruit Seed Formation",
      dataIndex: "Fruit_Seed_Formation",
      key: "Fruit_Seed_Formation",
      responsive: ["lg", "md", "xl", "sm"],
      render: (record) =>
        record === null ? "" : moment(record).format("DD/MM/YYYY"),
    },
    {
      title: "Fruit Seed Ripe",
      dataIndex: "Fruit_Seed_Ripe",
      key: "Fruit_Seed_Ripe",
      responsive: ["lg", "md", "xl", "sm"],
      render: (record) =>
        record === null ? "" : moment(record).format("DD/MM/YYYY"),
    },
    {
      title: "Leaf Bud Formation",
      dataIndex: "Leaf_bud_formation",
      key: "Leaf_bud_formation",
      responsive: ["lg", "md", "xl", "sm"],
      render: (record) =>
        record === null ? "" : moment(record).format("DD/MM/YYYY"),
    },
    {
      title: "Leaf Fall",
      dataIndex: "Leaf_fall",
      key: "Leaf_fall",
      responsive: ["lg", "md", "xl", "sm"],
      render: (record) =>
        record === null ? "" : moment(record).format("DD/MM/YYYY"),
    },

    {
      title: "Open Flower",
      dataIndex: "Open_Flower",
      key: "Open_Flower",
      responsive: ["lg", "md", "xl", "sm"],
      render: (record) =>
        record === null ? "" : moment(record).format("DD/MM/YYYY"),
    },
    {
      title: "Created Date",
      dataIndex: "Created_Dt",
      key: "Created_Dt",
      responsive: ["lg", "md", "xl", "sm"],
      render: (text) => (
        <p>
          {text === null ? "" : moment(text).format("DD/MM/YYYY hh:mm:ss A")}
        </p>
      ),
    },
    {
      title: "Created By",
      key: "Created_Dt",
      responsive: ["lg", "md", "xl", "sm"],
      render: (record) => <p>{record?.created_by?.Login_Name}</p>,
    },
    {
      title: "Modified At",
      dataIndex: "Modified_Dt",
      key: "Modified_Dt",
      responsive: ["lg", "md", "xl", "sm"],
      render: (text) => (
        <p>
          {text === null ? "" : moment(text).format("DD/MM/YYYY hh:mm:ss A")}
        </p>
      ),
    },
    {
      title: "Modified By",
      key: "Modify_Dt",
      responsive: ["lg", "md", "xl", "sm"],
      render: (record) => <p>{record?.modified_by?.Login_Name}</p>,
    },

    {
      title: "Action",
      key: "action",
      width: 100,
      fixed: "right",
      render: (rowData, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <AntButton
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => {
                console.log(rowData, record);
                navigate(`/plant-monitoring/${searchQuery}`, {
                  state: rowData,
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (location.state) {
      console.log(location.state);
      handleSuggestionInput(location.state);
      plantDetailsMutation.mutateAsync(location.state);
    }
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={0.5}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
                color="white"
              >
                <CardHeader
                  action={
                    <MDButton
                      component={Link}
                      aria-label="navigation"
                      to="/plant-monitoring"
                      startIcon={<AddIcon />}
                      //   onClick={() => navigate('/plant-survey', { replace: true })}
                    >
                      Add Monitoring
                    </MDButton>
                  }
                  title="Monitoring List"
                />
                {/* <MDTypography variant="h6" color="white">
                  Plant Master
                </MDTypography> */}
              </MDBox>
              <MDBox py={2} px={2}>
                <Autocomplete
                  size="small"
                  sx={{ width: "400px" }}
                  id="asynchronous-customerList"
                  open={open}
                  value={searchQuery}
                  onOpen={() => {
                    setOpen(true);
                  }}
                  onClose={() => {
                    setOpen(false);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.label
                  }
                  // getOptionLabel={(option) => option.title}
                  options={options}
                  loading={isLoading}
                  onChange={(event, newValue) => {
                    handleAutocompleteChange(newValue, false);
                  }}
                  onInputChange={(event, value) =>
                    debounceHandlerForSuggestions(value)
                  }
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      id="asynchronous-plant-suggestions"
                      label="Search Here..."
                      name="customerList"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {isLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </MDBox>
              <MDBox pt={1}>
                <TableContainer sx={{ boxShadow: "none" }}>
                  <Table
                    scroll={{
                      x: 4500,
                      y: 350,
                    }}
                    columns={columns}
                    dataSource={
                      plantDetailsMutation?.data?.data?.plant_monitoring_dtls
                    }
                    loading={isLoading}
                    pagination={{
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`,
                    }}
                  />
                </TableContainer>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default PlantMonitoringTable;
